import React from "react"

import classNames from "classnames"

import { toggle } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndex } from "shared/lib/useRovingTabIndex"
import { OnFocusedItemChange } from "shared/types/RovingTabIndex"
import { OnFocusedOptionChange, SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { useIsOptionDisabled } from "shared/ui/input/select/hooks/useIsOptionDisabled"

import { CheckboxListCheckbox } from "./CheckboxListCheckbox"
import { LoadingCheckboxes } from "./LoadingCheckboxes"

export interface CheckboxListProps<T> extends WithClassName {
  id?: string
  label: string
  options?: SelectOption<T>[] | undefined
  value?: T[] | null
  onChange?: (value: T[] | null) => void
  onFocusChange?: OnFocusedOptionChange<T>
  disabledOptions?: T[] | null | undefined
  disabled?: boolean
  loading?: boolean
  autoFocus?: boolean
}

function CheckboxListComponent<T>(
  {
    id,
    disabled,
    label,
    value,
    options,
    disabledOptions,
    loading,
    autoFocus,
    className,
    onChange,
    onFocusChange
  }: CheckboxListProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const handleChange = useEvent((changedValue: T) => {
    onChange?.(toggle(changedValue, value || []))
  })

  const isOptionDisabled = useIsOptionDisabled(disabledOptions)

  const handleFocusChange: OnFocusedItemChange = useEvent(
    ({ index, total }) => {
      const focusedOption = options?.[index]
      if (focusedOption) {
        onFocusChange?.({
          index,
          option: focusedOption,
          total,
          isSelected: !!value?.includes(focusedOption.value),
          selectedOptionTotal: value?.length
        })
      }
    }
  )

  const { containerProps, getElementProps } = useRovingTabIndex<
    HTMLInputElement,
    HTMLFieldSetElement
  >({
    elementCount: options?.length || 0,
    ref,
    onFocusedElementChange: handleFocusChange
  })

  return (
    <fieldset
      id={id}
      className={classNames(className, "checkbox-list")}
      {...containerProps}
    >
      {label ? <legend className="visually-hidden">{label}</legend> : null}
      {loading ? <LoadingCheckboxes amount={6} /> : null}

      {!loading
        ? options?.map((option, index) => (
            <CheckboxListCheckbox
              key={JSON.stringify(option.value)}
              value={value?.includes(option.value)}
              onChange={handleChange}
              option={option}
              disabled={disabled || isOptionDisabled(option)}
              autoFocus={index === 0 && autoFocus}
              {...getElementProps(index)}
            />
          ))
        : null}
    </fieldset>
  )
}
export const CheckboxListControl = React.memo(
  React.forwardRef(CheckboxListComponent)
)
